import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminChat } from "@/contexts/AdminChatContext";
import { useCopilotPlans } from "@/hooks/useCopilotPlans";
import { usePendingApprovalCount } from "@/hooks/usePendingApprovalCount";
import PlanTurn from "@/components/admin_dashboard/workflow_builder/copilot/PlanTurn";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import ContactsImportDialog from "@/components/admin_dashboard/ContactsImportDialog";
import {
  ArrowUp,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Loader2,
  Users,
  Target,
  CalendarDays,
  MessageSquare,
  Plus,
  Upload,
  Plug,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// The pending-actions capsule is hidden for now. Flip to true to restore it.
const SHOW_PENDING_ACTIONS = false;

// A previously-started conversation, as listed in "Conversation history".
interface ChatThread {
  id: string;
  title: string | null;
  updated_at: string;
}

// Mentionable targets. `mention` is the exact text used in @mentions and
// prompts; `colorClass` tints the @name wherever it appears.
type Agent = {
  id: string;
  mention: string;
  colorClass: string;
};

const AGENTS: Agent[] = [
  { id: "everyone", mention: "everyone", colorClass: "text-foreground/70" },
];

function agentColor(mention: string): string {
  return AGENTS.find((a) => a.mention === mention)?.colorClass ?? "text-foreground/70";
}

// Matches an @mention for any known target, longest names first so a longer
// mention wins over a shorter one it starts with.
const MENTION_RE = new RegExp(
  `@(?:${[...AGENTS]
    .sort((a, b) => b.mention.length - a.mention.length)
    .map((a) => a.mention.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|")})`,
  "g",
);

// If the caret sits inside an "@…" token (at line start or after whitespace),
// returns the token's start index and the text typed after the "@". Used to
// drive the mention autocomplete.
function getMentionQuery(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(text[at - 1])) return null;
  const query = upto.slice(at + 1);
  if (query.includes("\n")) return null;
  return { start: at, query };
}

// Splits raw input into plain text and colored @mention segments for the
// highlight overlay rendered behind the textarea.
function highlightMentions(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      // Color only. Must keep the textarea's font weight so glyph widths
      // match and the (transparent) caret stays aligned with this overlay.
      <span key={m.index} className={agentColor(m[0].slice(1))}>
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

type Suggestion = {
  label: string;
  prompt: string;
  icon: typeof Users;
};

const SUGGESTIONS: Suggestion[] = [
  {
    label: "About a contact",
    prompt: "Tell me more about ",
    icon: Users,
  },
  {
    label: "Close a lead",
    prompt: "What's the best way to close ",
    icon: Target,
  },
  {
    label: "Booking recap",
    prompt: "Give me a recap of this week's bookings.",
    icon: CalendarDays,
  },
  {
    label: "Draft follow-up",
    prompt: "Draft a follow-up message for ",
    icon: MessageSquare,
  },
];

// Compact relative timestamp for the conversation-history rows ("just now",
// "3h", "2d"), falling back to a short date once past a week.
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

interface HomeChatProps {
  onNavigateToSection?: (section: string, extraParams?: Record<string, string>) => void;
}

const HomeChat = ({ onNavigateToSection }: HomeChatProps = {}) => {
  const { user, businessId } = useAuth();
  const firstName =
    user?.user_metadata?.first_name ||
    user?.user_metadata?.full_name?.split(" ")[0] ||
    "there";

  // Pending-approvals count. A glanceable capsule so the business sees what
  // needs human review without navigating to Actions. Refetches every 60s.
  const pendingApprovals = usePendingApprovalCount(businessId);

  const queryClient = useQueryClient();

  // Account state for the dynamic Home start screen: real contact count, whether
  // any channel is live, and a few recent contact names to personalise prompts.
  // Replaces the old hardcoded "chat history" placeholder.
  const { data: home } = useQuery({
    queryKey: ["home-start", businessId],
    queryFn: async () => {
      if (!businessId)
        return { contactCount: 0, channelCount: 0, recentNames: [] as string[] };
      const [contacts, channels, recent] = await Promise.all([
        supabase
          .from("customers")
          .select("customer_id", { count: "exact", head: true })
          .eq("business_id", businessId),
        supabase
          .from("business_channel_connections")
          .select("id", { count: "exact", head: true })
          .eq("business_id", businessId)
          // The connection table's live status is "connected" (see
          // _shared/connectors/credentials.ts ChannelConnection.status); there
          // is no "active" value, so the old filter always counted zero and
          // could mis-flag an account with a channel as brand-new.
          .eq("status", "connected"),
        supabase
          .from("customers")
          .select("name")
          .eq("business_id", businessId)
          .order("created_at", { ascending: false })
          .limit(3),
      ]);
      if (contacts.error) throw contacts.error;
      if (channels.error) throw channels.error;
      if (recent.error) throw recent.error;
      return {
        contactCount: contacts.count ?? 0,
        channelCount: channels.count ?? 0,
        recentNames: ((recent.data ?? []) as { name: string | null }[])
          .map((r) => r.name)
          .filter((n): n is string => !!n && n.trim().length > 0),
      };
    },
    enabled: !!businessId,
  });

  // Previous conversations for the "Conversation history" list. Reads the
  // owner's own threads directly (authenticated + RLS on admin_chat_threads);
  // the admin-assistant edge fn is what writes them. Most-recently-active first.
  const { data: chatHistory = [] } = useQuery({
    queryKey: ["chat-history", businessId],
    queryFn: async () => {
      if (!businessId) return [] as ChatThread[];
      const { data, error } = await supabase
        .from("admin_chat_threads")
        .select("id, title, updated_at")
        .eq("business_id", businessId)
        .order("updated_at", { ascending: false })
        .limit(25);
      if (error) throw error;
      return (data ?? []) as ChatThread[];
    },
    enabled: !!businessId,
  });

  const contactCount = home?.contactCount ?? 0;
  const channelCount = home?.channelCount ?? 0;
  const recentNames = home?.recentNames ?? [];
  // "New" account = nothing imported and no channel connected → lead with
  // get-started actions instead of customer-centric prompts they can't use yet.
  const isNewAccount = home !== undefined && contactCount === 0 && channelCount === 0;

  const [importOpen, setImportOpen] = useState(false);
  // Conversation history is capped at 5 rows until the owner expands it.
  const [showAllHistory, setShowAllHistory] = useState(false);

  const [input, setInput] = useState("");
  // The conversation itself lives in AdminChatContext so the docked panel in
  // Workflows and this tab are two views of ONE chat, not two chat systems.
  const {
    messages,
    isThinking,
    followUps,
    hasConversation,
    send: sendMessage,
    openThread: openSharedThread,
    newChat: resetConversation,
  } = useAdminChat();

  // Workflow plans render here too. This tab and the docked panel in Workflows
  // are two views of one conversation, so expanding a plan into Home must not
  // drop the plan card and its Build button.
  const { pendingPlanId, committingId, buildPlan } = useCopilotPlans({
    onWorkflowCreated: (workflowId) =>
      onNavigateToSection?.("workflows", { builder: workflowId }),
  });

  // Mention autocomplete state.
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [mentionDismissed, setMentionDismissed] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);


  // Active "@…" token at the caret and the agents matching it.
  const mentionInfo = getMentionQuery(input, caret);
  const mentionMatches = mentionInfo
    ? AGENTS.filter((a) =>
        a.mention.toLowerCase().startsWith(mentionInfo.query.toLowerCase()),
      )
    : [];
  const mentionMenuOpen = !mentionDismissed && mentionMatches.length > 0;

  // Reset the highlighted row whenever the query changes.
  useEffect(() => {
    setHighlight(0);
  }, [mentionInfo?.query]);

  const insertMention = (agent: Agent) => {
    const el = textareaRef.current;
    if (!el) return;
    const info = getMentionQuery(input, el.selectionStart ?? caret);
    if (!info) return;
    const before = input.slice(0, info.start);
    const after = input.slice(el.selectionStart ?? caret);
    const inserted = `@${agent.mention} `;
    const next = before + inserted + after;
    const pos = (before + inserted).length;
    setInput(next);
    setMentionDismissed(false);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking]);

  // Auto-grow the textarea up to a cap.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  // Thin wrapper over the shared store: this view owns only the composer text.
  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || isThinking) return;
    setInput("");
    await sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention menu navigation takes priority while it's open.
    if (mentionMenuOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % mentionMatches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight(
          (h) => (h - 1 + mentionMatches.length) % mentionMatches.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionMatches[highlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionDismissed(true);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleSuggestion = (s: Suggestion) => {
    // Open-ended prompts end with a space so the user can finish the thought;
    // complete prompts are sent immediately.
    if (s.prompt.endsWith(" ")) {
      setInput(s.prompt);
      textareaRef.current?.focus();
    } else {
      send(s.prompt);
    }
  };

  const composer = (
    <div className="rounded-xl border border-input bg-card shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background transition-shadow">
      <div className="relative">
        {/* Mirror layer: renders the same text behind the (transparent)
            textarea so @mentions can be color-coded as the user types. */}
        <div
          ref={highlightRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-4 py-3.5 text-sm font-body text-foreground"
        >
          {highlightMentions(input)}
          {"\n"}
        </div>
        <Textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
            setMentionDismissed(false);
          }}
          onKeyDown={handleKeyDown}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onScroll={(e) => {
            if (highlightRef.current)
              highlightRef.current.scrollTop = e.currentTarget.scrollTop;
          }}
          placeholder="Ask anything..."
          className="relative min-h-[56px] resize-none border-0 bg-transparent px-4 py-3.5 text-sm font-body text-transparent caret-foreground shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          rows={1}
        />
        {mentionMenuOpen && (
          <div className="absolute left-3 top-full z-20 mt-1 w-60 overflow-hidden rounded-lg border border-border bg-popover py-1 shadow-md">
            {mentionMatches.map((a, i) => (
              <button
                key={a.id}
                type="button"
                // onMouseDown (not onClick) so the textarea keeps focus.
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(a);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "flex w-full items-center px-3 py-1.5 text-left text-sm transition-colors",
                  i === highlight ? "bg-muted" : "bg-transparent",
                )}
              >
                <span className={cn("font-medium", a.colorClass)}>
                  @{a.mention}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 px-3 pb-3">
        <Button
          type="button"
          size="icon"
          className="h-8 w-8 rounded-lg"
          onClick={() => send()}
          disabled={!input.trim() || isThinking}
          aria-label="Send"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const pendingCapsule = (
    <button
      type="button"
      onClick={() => onNavigateToSection?.("action-center")}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
        pendingApprovals > 0
          ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
          : "border-border bg-muted/30 text-muted-foreground hover:bg-muted/50"
      } ${onNavigateToSection ? "cursor-pointer" : "cursor-default"}`}
      disabled={!onNavigateToSection}
    >
      <ShieldCheck className="h-3.5 w-3.5" />
      <span className="font-medium">
        {pendingApprovals === 0
          ? "All actions reviewed"
          : `${pendingApprovals} action${pendingApprovals === 1 ? "" : "s"} pending approval`}
      </span>
      {pendingApprovals > 0 && onNavigateToSection && <ArrowRight className="h-3.5 w-3.5" />}
    </button>
  );

  // Reset to the empty Home chat interface (greeting + composer), then focus
  // the composer so the user can start typing right away.
  const newChat = () => {
    resetConversation();
    setInput("");
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  // Re-open a past conversation from the history list: load its turns (oldest
  // first) into the thread view and adopt its thread id so follow-ups continue
  // the same conversation server-side.
  const openThread = async (id: string) => {
    setInput("");
    await openSharedThread(id);
  };

  // Brand-new account: lead with the two real next steps (both fully wired —
  // "Import" opens the importer dialog inline; "Connect" opens connector settings).
  const startActions = [
    {
      icon: Plug,
      title: "Connect a channel",
      desc: "Link WhatsApp, Telegram, Instagram, Messenger, or LINE so your AI can chat with customers.",
      onClick: () => onNavigateToSection?.("settings", { settings: "connectors" }),
    },
    {
      icon: Upload,
      title: "Import your contacts",
      desc: "Bring your existing customer list in so Dibs can build profiles and re-engage them.",
      onClick: () => setImportOpen(true),
    },
  ];

  const getStartedSection = (
    <div className="space-y-2 pt-2">
      <p className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Get started
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {startActions.map((a) => (
          <button
            key={a.title}
            type="button"
            onClick={a.onClick}
            className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/50"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <a.icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">{a.title}</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                {a.desc}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  // Account with data: tailored prompt chips that name real contacts. These feed
  // the composer so the owner can adjust the wording before sending.
  // Deduped by label: two contacts sharing a first name otherwise render
  // identical chips with duplicate React keys (QA F-A5-2).
  const namePrompts = recentNames
    .map((name) => ({
      label: `About ${name.split(" ")[0]}`,
      prompt: `Tell me more about ${name}.`,
    }))
    .filter((p, i, arr) => arr.findIndex((q) => q.label === p.label) === i)
    .slice(0, 2);
  const dynamicPrompts: { label: string; prompt: string }[] = [
    ...namePrompts,
    { label: "Who's gone quiet?", prompt: "Which of my contacts haven't booked or replied recently?" },
  ];

  // AI-tailored prompts lead the row; the four static defaults follow so the
  // owner still has the standard actions on hand.
  const suggestedSection = (
    <div className="space-y-2 pt-2">
      <p className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Suggested for you
      </p>
      <div className="flex flex-wrap gap-2">
        {dynamicPrompts.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => {
              setInput(p.prompt);
              textareaRef.current?.focus();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
            {p.label}
          </button>
        ))}
        {SUGGESTIONS.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => handleSuggestion(s)}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            <s.icon className="h-3.5 w-3.5 shrink-0" />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );

  // Renders a single history row (used for both the visible rows and the
  // blurred 6th teaser that sits behind the "View all" button).
  const historyRow = (t: ChatThread) => (
    <button
      key={t.id}
      type="button"
      onClick={() => openThread(t.id)}
      className="group flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-left transition-colors hover:border-border hover:bg-muted/50"
    >
      <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        {t.title?.trim() || "Untitled chat"}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {relativeTime(t.updated_at)}
      </span>
    </button>
  );

  // Past conversations, most-recently-active first. Clicking a row re-opens the
  // thread in the chat view. Hidden until the owner has started at least one.
  // Collapsed to 5 rows, with a "View all" toggle below.
  const hasMoreHistory = chatHistory.length > 5;
  const conversationHistorySection =
    chatHistory.length > 0 ? (
      <div className="space-y-2 pt-2">
        <p className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Conversation history
        </p>
        <div className="flex flex-col gap-1">
          {(showAllHistory ? chatHistory : chatHistory.slice(0, 5)).map(historyRow)}
        </div>
        {hasMoreHistory && (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={() => setShowAllHistory((v) => !v)}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary/80"
            >
              {showAllHistory ? "Show less" : "View all conversations"}
              {showAllHistory ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        )}
      </div>
    ) : null;

  if (!hasConversation) {
    return (
      <div className="flex h-full flex-col items-center overflow-y-auto pt-16 pb-8">
        {SHOW_PENDING_ACTIONS && (
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            {pendingCapsule}
          </div>
        )}
        <div className="w-full max-w-2xl space-y-6">
          <h1 className="text-center text-3xl font-heading font-semibold text-foreground">
            {getGreeting()}, {firstName}
          </h1>
          {composer}
        </div>
        <div className="mt-8 w-full max-w-2xl space-y-6">
          {isNewAccount ? getStartedSection : suggestedSection}
          {conversationHistorySection}
        </div>
        {businessId && (
          <ContactsImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            businessId={businessId}
            onImported={() =>
              queryClient.invalidateQueries({ queryKey: ["home-start", businessId] })
            }
          />
        )}
      </div>
    );
  }

  // Chat title. The first user message, mirroring the thread title the
  // backend derives when it creates the admin_chat_threads row.
  const chatTitle = messages.find((m) => m.role === "user")?.content ?? "New chat";

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-border py-2">
        <h2 className="min-w-0 truncate text-sm font-medium text-foreground">{chatTitle}</h2>
        <button
          type="button"
          onClick={newChat}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          New chat
        </button>
      </div>
      <div className="flex-1 space-y-6 overflow-y-auto py-6">
        {messages.map((m) => (
          <div key={m.id} className="space-y-3">
            <div
              className={cn(
                "flex gap-3",
                m.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              {m.role === "assistant" && (
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Sparkles className="h-4 w-4" />
                </div>
              )}
              <div
                className={cn(
                  "max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm",
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                )}
              >
                {m.content}
              </div>
            </div>
            {/* Aligned under the assistant avatar, and full-width: a plan is a
                document to vet, not a chat bubble to skim. */}
            <div className="pl-10">
              <PlanTurn
                message={m}
                pendingPlanId={pendingPlanId}
                committingId={committingId}
                onBuild={buildPlan}
                onRevise={() => textareaRef.current?.focus()}
                onOpenWorkflow={(workflowId) =>
                  onNavigateToSection?.("workflows", { builder: workflowId })
                }
                onUseSuggestion={(text) => send(text)}
              />
            </div>
          </div>
        ))}
        {isThinking && (
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex items-center gap-2 rounded-2xl bg-muted px-4 py-2.5 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Thinking…
            </div>
          </div>
        )}
        {/* Proactive follow-ups from the latest assistant reply — one tap asks. */}
        {!isThinking && followUps.length > 0 && (
          <div className="flex flex-wrap gap-2 pl-10">
            {followUps.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => send(s)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
                {s}
              </button>
            ))}
          </div>
        )}
        <div ref={threadEndRef} />
      </div>
      <div className="pb-4">{composer}</div>
    </div>
  );
};

export default HomeChat;
